import {
  Box, TextField, Typography, Button, TableContainer, Table,
  TableHead, TableRow, TableCell, TableBody, IconButton,
  Collapse, Avatar, Chip, Snackbar, Alert, Divider, InputAdornment,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { SideBarWidth } from "../../utils/SideBarWidth";
import { hub } from "../../api/requests/hubs/hubs";
import { Colors } from "../../Theme/Theme";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";

const UsersDetailsWallet = () => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [passwordValue, setPasswordValue] = useState("");
  const [amount, setAmount] = useState("");
  const [data, setData] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [search, setSearch] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const getUsersWallet = async () => {
    try {
      const response = await hub.getUsersWallet();
      setData(response?.users || []);
    } catch (error) {
      setSnackbar({ open: true, message: error?.response?.data || "Failed to load data", severity: "error" });
    }
  };

  useEffect(() => {
    getUsersWallet();
  }, []);

  const handleSave = async () => {
    if (!selectedItem) {
      setSnackbar({ open: true, message: "Please select a user first.", severity: "warning" });
      return;
    }
    try {
      const response = await hub.patchUpdateUserPassword(selectedId, { newPassword: passwordValue });
      if (response) {
        setSelectedItem(null);
        setPasswordValue("");
        setSnackbar({ open: true, message: "Password updated successfully", severity: "success" });
      }
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to update password", severity: "error" });
    }
  };

  const handleAddAmount = async () => {
    if (!selectedItem) {
      setSnackbar({ open: true, message: "Please select a user first.", severity: "warning" });
      return;
    }
    try {
      const response = await hub.patchUpdateWalletDetails({ amount: Number(amount), user_id: selectedId });
      if (response) {
        setSelectedItem(null);
        setAmount("");
        getUsersWallet();
        setSnackbar({ open: true, message: "Amount added successfully", severity: "success" });
      }
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to add amount", severity: "error" });
    }
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setSelectedId(item?.user?.id);
  };

  const calculateTotalAmount = (walletHistory) => {
    if (!walletHistory || walletHistory.length === 0) return 0;
    return walletHistory.reduce((total, t) => {
      const amt = parseFloat(t.amount) || 0;
      if (t.type.toLowerCase() === "credit") return total + amt;
      if (t.type.toLowerCase() === "withdrawal" || t.type.toLowerCase() === "debit") return total - amt;
      return total;
    }, 0);
  };

  const filteredData = data.filter((item) => {
    const q = search.toLowerCase();
    return (
      item.user?.name?.toLowerCase().includes(q) ||
      item.user?.emailId?.toLowerCase().includes(q) ||
      item.user?.shortcode?.toLowerCase().includes(q)
    );
  });

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px", fontSize: "14px",
      "& fieldset": { borderColor: Colors.dividerColor },
      "&:hover fieldset": { borderColor: Colors.primary },
      "&.Mui-focused fieldset": { borderColor: Colors.primary, borderWidth: "2px" },
    },
    "& .MuiInputLabel-root": { fontSize: "13px", color: Colors.secondaryColor },
    "& .MuiInputLabel-root.Mui-focused": { color: Colors.primary },
  };

  return (
    <Box sx={{
      width: { xs: "100%", md: `calc(100% - ${SideBarWidth}px)` },
      ml: { md: `${SideBarWidth}px` },
      minHeight: "100vh", backgroundColor: Colors.bgColor,
    }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <AccountBalanceWalletIcon sx={{ fontSize: 22, color: Colors.primary }} />
            <Typography sx={{ fontWeight: 700, color: Colors.primaryTextColor, fontSize: { xs: "20px", md: "24px" }, letterSpacing: "-0.02em" }}>
              Users Wallet
            </Typography>
          </Box>
          <Typography sx={{ color: Colors.secondaryColor, fontSize: "13px" }}>
            Manage user wallets, update passwords, and add funds.
          </Typography>
        </Box>

        {/* Action Bar */}
        {selectedItem && (
          <Box sx={{
            backgroundColor: Colors.white, borderRadius: "12px",
            border: `1px solid ${Colors.blueBorder}`, p: 2.5, mb: 3,
            background: `linear-gradient(135deg, ${Colors.blueSoft} 0%, #FFFFFF 100%)`,
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <Avatar sx={{ width: 36, height: 36, backgroundColor: Colors.primary, color: Colors.white, fontSize: 14, fontWeight: 600 }}>
                {(selectedItem?.name || selectedItem?.user?.name || "U").charAt(0).toUpperCase()}
              </Avatar>
              <Box>
                <Typography sx={{ fontSize: "14px", fontWeight: 600, color: Colors.primaryTextColor }}>
                  {selectedItem?.name || selectedItem?.user?.name}
                </Typography>
                <Typography sx={{ fontSize: "11px", fontWeight: 400, color: Colors.secondaryColor }}>
                  {selectedItem?.user?.emailId || ""}
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={() => { setSelectedItem(null); setPasswordValue(""); setAmount(""); }}
                sx={{ ml: "auto", fontSize: "11px", color: Colors.secondaryColor, textTransform: "none" }}
              >
                Deselect
              </Button>
            </Box>
            <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2 }}>
              <Box sx={{ display: "flex", gap: 1.5, flex: 1 }}>
                <TextField
                  size="small" label="New Password" type="password"
                  value={passwordValue}
                  onChange={(e) => setPasswordValue(e.target.value)}
                  sx={{ ...fieldSx, flex: 1 }}
                />
                <Button
                  variant="contained" onClick={handleSave}
                  disabled={!passwordValue}
                  sx={{
                    backgroundColor: Colors.primary, fontWeight: 600, fontSize: "13px",
                    borderRadius: "10px", px: 3, textTransform: "none", boxShadow: "none",
                    "&:hover": { backgroundColor: Colors.hoverColorBtn },
                    "&.Mui-disabled": { backgroundColor: Colors.dividerColor },
                  }}
                >
                  Update Password
                </Button>
              </Box>
              <Box sx={{ display: "flex", gap: 1.5, flex: 1 }}>
                <TextField
                  size="small" label="Amount (Rs.)" type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  sx={{ ...fieldSx, flex: 1 }}
                />
                <Button
                  variant="contained" onClick={handleAddAmount}
                  disabled={!amount}
                  sx={{
                    backgroundColor: "#059669", fontWeight: 600, fontSize: "13px",
                    borderRadius: "10px", px: 3, textTransform: "none", boxShadow: "none",
                    "&:hover": { backgroundColor: "#047857" },
                    "&.Mui-disabled": { backgroundColor: Colors.dividerColor },
                  }}
                >
                  Add Funds
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* Search */}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth placeholder="Search users by name, email, or shortcode..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: Colors.secondaryColor, fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{ ...fieldSx, maxWidth: 420, "& .MuiOutlinedInput-root": { ...fieldSx["& .MuiOutlinedInput-root"], backgroundColor: Colors.white } }}
          />
        </Box>

        {/* Table */}
        <Box sx={{
          backgroundColor: Colors.white, borderRadius: "12px",
          border: `1px solid ${Colors.dividerColor}`, overflow: "hidden",
        }}>
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow>
                  {["", "User", "Shortcode", "Wallet Balance", "Level", "Hub", "Actions"].map((h) => (
                    <TableCell key={h} sx={{
                      fontWeight: 600, fontSize: "11px", color: Colors.secondaryColor,
                      textTransform: "uppercase", letterSpacing: "0.5px", py: 1.5,
                      backgroundColor: "#F9FBFC", borderBottom: `1px solid ${Colors.dividerColor}`,
                      whiteSpace: "nowrap",
                    }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: "center", py: 6 }}>
                      <Typography sx={{ color: Colors.secondaryColor, fontSize: "14px" }}>
                        {search ? "No users match your search" : "No users found"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.map((item) => {
                    const isExpanded = expandedRow === item.user.id;
                    const balance = calculateTotalAmount(item?.walletHistory);
                    return (
                      <React.Fragment key={item.user.id}>
                        <TableRow
                          onClick={() => setExpandedRow(isExpanded ? null : item.user.id)}
                          sx={{ cursor: "pointer", "&:hover": { backgroundColor: "#FAFBFF" }, transition: "background 0.15s" }}
                        >
                          <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}`, width: 40, px: 1 }}>
                            <IconButton size="small">
                              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}` }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                              <Avatar sx={{ width: 32, height: 32, backgroundColor: Colors.blueSoft, color: Colors.primary, fontSize: 13, fontWeight: 600 }}>
                                {(item.user.name || "U").charAt(0).toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: Colors.primaryTextColor, lineHeight: 1.3 }}>
                                  {item.user.name}
                                </Typography>
                                <Typography sx={{ fontSize: "11px", fontWeight: 400, color: Colors.secondaryColor }}>
                                  {item.user.emailId}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}` }}>
                            <Chip label={item.user.shortcode} size="small" sx={{
                              height: 22, fontSize: "11px", fontWeight: 600,
                              backgroundColor: Colors.blueSoft, color: Colors.primary,
                              "& .MuiChip-label": { px: 0.8 },
                            }} />
                          </TableCell>
                          <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}` }}>
                            <Typography sx={{ fontSize: "13px", fontWeight: 400, color: "#059669" }}>
                              Rs. {balance.toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}` }}>
                            <Typography sx={{ fontSize: "13px", fontWeight: 400, color: "#059669" }}>
                              {item.level}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}` }}>
                            <Typography sx={{ fontSize: "13px", fontWeight: 400, color: Colors.primaryTextColor }}>
                              {item.hubName}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}` }}>
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); handleEdit(item); }}
                              sx={{ backgroundColor: Colors.blueSoft, "&:hover": { backgroundColor: Colors.blueMid } }}
                            >
                              <ModeEditOutlineOutlinedIcon sx={{ color: Colors.primary, fontSize: 18 }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>

                        {/* Expanded row */}
                        <TableRow>
                          <TableCell colSpan={7} sx={{ p: 0, borderBottom: isExpanded ? `1px solid ${Colors.dividerColor}` : "none" }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2.5, backgroundColor: "#FAFBFF" }}>
                                <Box sx={{
                                  display: "flex", justifyContent: "space-between", alignItems: "center",
                                  mb: 2, p: 2, backgroundColor: Colors.white, borderRadius: "10px",
                                  border: `1px solid ${Colors.dividerColor}`,
                                }}>
                                  <Typography sx={{ fontSize: "13px", fontWeight: 400, color: Colors.secondaryColor }}>
                                    Total Wallet Balance
                                  </Typography>
                                  <Typography sx={{ fontSize: "16px", fontWeight: 400, color: balance >= 0 ? "#059669" : "#EF4444" }}>
                                    Rs. {balance.toFixed(2)}
                                  </Typography>
                                </Box>

                                <Box sx={{
                                  backgroundColor: Colors.white, borderRadius: "10px",
                                  border: `1px solid ${Colors.dividerColor}`, overflow: "hidden",
                                }}>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        {["Transaction ID", "Amount", "Type", "Date"].map((h) => (
                                          <TableCell key={h} sx={{
                                            fontWeight: 600, fontSize: "11px", color: Colors.secondaryColor,
                                            textTransform: "uppercase", letterSpacing: "0.5px",
                                            backgroundColor: "#F9FBFC", py: 1.2,
                                            borderBottom: `1px solid ${Colors.dividerColor}`,
                                          }}>
                                            {h}
                                          </TableCell>
                                        ))}
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {item?.walletHistory?.length === 0 ? (
                                        <TableRow>
                                          <TableCell colSpan={4} sx={{ textAlign: "center", py: 3 }}>
                                            <Typography sx={{ fontSize: "13px", fontWeight: 400, color: Colors.secondaryColor }}>No transactions yet</Typography>
                                          </TableCell>
                                        </TableRow>
                                      ) : (
                                        item?.walletHistory?.map((h) => {
                                          const isCredit = h.type?.toLowerCase() === "credit";
                                          return (
                                            <TableRow key={h.id}>
                                              <TableCell sx={{ fontSize: "12px", fontWeight: 400, color: Colors.primaryTextColor, borderBottom: `1px solid ${Colors.dividerColor}` }}>
                                                {h.id}
                                              </TableCell>
                                              <TableCell sx={{ fontSize: "12px", fontWeight: 400, color: isCredit ? "#059669" : "#EF4444", borderBottom: `1px solid ${Colors.dividerColor}` }}>
                                                {isCredit ? "+" : "-"} Rs. {h.amount}
                                              </TableCell>
                                              <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}` }}>
                                                <Chip label={h.type} size="small" sx={{
                                                  height: 20, fontSize: "10px", fontWeight: 600,
                                                  backgroundColor: isCredit ? Colors.greenSoft : "#FEF2F2",
                                                  color: isCredit ? "#059669" : "#EF4444",
                                                  "& .MuiChip-label": { px: 0.6 },
                                                }} />
                                              </TableCell>
                                              <TableCell sx={{ fontSize: "12px", fontWeight: 400, color: Colors.secondaryColor, borderBottom: `1px solid ${Colors.dividerColor}` }}>
                                                {new Date(h.timestamp).toLocaleString("en-IN")}
                                              </TableCell>
                                            </TableRow>
                                          );
                                        })
                                      )}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled" sx={{ borderRadius: "10px" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UsersDetailsWallet;
